import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { Button, Input, MessageBox } from "@/components/ui";
import { AUTH_CHALLENGES } from "@/constants/auth";
import { useAuth } from "@/context/auth/AuthContext";
import { usePortalNavigationAccess } from "@/hooks/usePortalNavigationAccess";
import { useToast } from "@/hooks/useToast";
import { AuthPageLayout } from "@/layouts/AuthPageLayout";
import { LOGIN_PAGE } from "@/router/paths";
const mfaChallengeSchema = z.object({
    code: z.string().min(6, "Code must be 6 digits").max(6, "Code must be 6 digits").regex(/^[0-9]+$/, "Code must contain only numbers")
});
export default function MfaChallengePage() {
    const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
        resolver: zodResolver(mfaChallengeSchema)
    });
    const { submitMfaChallenge, challengeData, error: authError, isAuthenticated, clearError } = useAuth();
    const toast = useToast();
    const navigate = useNavigate();
    const { defaultPage, isLoading: isNavigationAccessLoading } = usePortalNavigationAccess(isAuthenticated);
    const [submitError, setSubmitError] = useState(null);
    useEffect(()=>{
        // If already authenticated, redirect to the product-appropriate landing page.
        if (isAuthenticated) {
            if (!isNavigationAccessLoading) {
                navigate(defaultPage);
            }
            return;
        }
        // If no MFA challenge data, redirect to login
        if (!challengeData || challengeData.challengeName !== AUTH_CHALLENGES.SOFTWARE_TOKEN_MFA && challengeData.challengeName !== AUTH_CHALLENGES.SMS_MFA) {
            navigate(LOGIN_PAGE);
            return;
        }
    }, [
        challengeData,
        defaultPage,
        isAuthenticated,
        isNavigationAccessLoading,
        navigate
    ]);
    useEffect(()=>{
        if (authError) {
            setSubmitError(authError);
            clearError();
        }
    }, [
        authError,
        clearError
    ]);
    const onSubmit = async (data)=>{
        if (!challengeData) {
            toast.error("Invalid session. Please log in again.");
            navigate(LOGIN_PAGE);
            return;
        }
        setSubmitError(null);
        try {
            await submitMfaChallenge({
                code: data.code
            });
        // Success - user will be redirected by AuthContext
        } catch (error) {
            if (error instanceof Error) {
                setSubmitError(error.message);
            } else {
                setSubmitError("Failed to verify MFA code");
            }
        }
    };
    // Don't render if no challenge data
    if (!challengeData || challengeData.challengeName !== AUTH_CHALLENGES.SOFTWARE_TOKEN_MFA && challengeData.challengeName !== AUTH_CHALLENGES.SMS_MFA) {
        return null;
    }
    const isSmsMfa = challengeData.challengeName === AUTH_CHALLENGES.SMS_MFA;
    return /*#__PURE__*/ _jsx(AuthPageLayout, {
        title: "Two-Factor Authentication",
        subtitle: isSmsMfa ? "Enter the verification code sent to your phone." : "Enter the 6-digit code from your authenticator app.",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "w-full",
            children: [
                /*#__PURE__*/ _jsxs("form", {
                    onSubmit: handleSubmit(onSubmit),
                    className: "w-full flex flex-col gap-4",
                    children: [
                        submitError && /*#__PURE__*/ _jsx(MessageBox, {
                            message: submitError,
                            type: "error"
                        }),
                        /*#__PURE__*/ _jsx(Input, {
                            label: "Verification Code",
                            type: "text",
                            inputMode: "numeric",
                            placeholder: "Enter 6-digit code",
                            maxLength: 6,
                            autoComplete: "one-time-code",
                            ...register("code"),
                            errors: errors.code
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            type: "submit",
                            variant: "simple",
                            color: "navy-90",
                            className: "w-full",
                            disabled: isSubmitting,
                            children: isSubmitting ? "Verifying..." : "Verify"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "mt-6 text-center",
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-xs text-gray-500",
                            children: isSmsMfa ? "Didn't receive the code? Please wait a moment or try logging in again." : "Open your authenticator app (like Google Authenticator or Authy) to get your code."
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-xs text-gray-500 mt-2",
                            children: "Lost access to your authentication device? Please contact support for assistance."
                        })
                    ]
                })
            ]
        })
    });
}
